/**
 * The tab catalogs of both project detail surfaces.
 *
 * There are two project pages — `pages/project-detail.tsx` for designers and
 * contractors, `components/homeowner/ho-active-project-detail.tsx` for
 * homeowners — and they read the SAME `?tab=` query parameter. A key therefore
 * means whatever the surface reading it says it means, and one surface cannot
 * decide what to do with a key without knowing what the other still calls live:
 * `milestones` is retired into Plan for a designer and a live tab for a
 * homeowner, `payments` the other way round.
 *
 * Both catalogs live here because of that dependency. Each surface imports its
 * own tab list and its own retired map, and the other surface's live keys for
 * `shouldRewriteTabKey`. Keeping them in the two page modules instead would
 * make those modules import each other.
 *
 * Each live key list is derived from the tab array it belongs to, so a tab
 * added to a surface is a live key on that surface without a second edit.
 *
 * Both catalogs name their label as an i18n key, which each surface renders as
 * `t(labelKey)`. A key with no entry in en.json renders as itself, and one
 * missing from hi.json falls back to the English value; a label added here
 * without both entries fails utils/__tests__/project-tab-keys.test.ts.
 */

import type { IconName } from "../types/icon-name";

// ---------------------------------------------------------------------------
// Designer and contractor surface
// ---------------------------------------------------------------------------

export type DesignerTabKey =
	| "overview"
	| "team"
	| "plan"
	| "payments"
	| "materials"
	| "messages"
	| "boq"
	| "documents";

// Tab order is fixed by docs/plans/2026-09-24-0700-refactor-project-detail-tabs-plan.md.
// The tab bar renders this array in array order, so this array is the order.
export const DESIGNER_TABS: { key: DesignerTabKey; labelKey: string }[] = [
	{ key: "overview", labelKey: "project.overview" },
	{ key: "plan", labelKey: "project.plan" },
	{ key: "boq", labelKey: "boq.tabLabel" },
	{ key: "materials", labelKey: "project.materials" },
	{ key: "documents", labelKey: "project.documents" },
	{ key: "payments", labelKey: "project.payments.title" },
	{ key: "messages", labelKey: "project.messages" },
	{ key: "team", labelKey: "project.team" },
];

export const DESIGNER_TAB_KEYS: readonly DesignerTabKey[] = DESIGNER_TABS.map(
	(tab) => tab.key,
);

// Keys this plan retired on THIS surface, each pointing at the tab that
// absorbed it. Notification emails already delivered and any bookmark still
// carry them, and resolveTabKey discards an unknown key, so without this map
// they land on Overview.
//
// `milestones` is retired here because Plan absorbed it, and it is a live tab
// on the homeowner surface, which is what shouldRewriteTabKey is for.
// `contractors` was never a tab on either surface; it is here because backend
// notifications built deep links with it (project-contractors.service.ts, now
// emitting `team`).
export const DESIGNER_RETIRED_TAB_KEYS: Readonly<
	Record<string, DesignerTabKey>
> = {
	comments: "messages",
	costs: "boq",
	tasks: "plan",
	milestones: "plan",
	activity: "overview",
	contractors: "team",
};

// ---------------------------------------------------------------------------
// Homeowner surface
// ---------------------------------------------------------------------------

export type HomeownerTabId =
	| "overview"
	| "team"
	| "messages"
	| "boq"
	| "milestones"
	| "selections"
	| "documents";

export interface HomeownerTabDef {
	id: HomeownerTabId;
	labelKey: string;
	icon: IconName;
	badge?: number;
}

export const HOMEOWNER_TABS: HomeownerTabDef[] = [
	{ id: "overview", labelKey: "project.overview", icon: "grid" },
	{ id: "messages", labelKey: "project.messages", icon: "message" },
	{ id: "boq", labelKey: "boq.tabLabel", icon: "fileText" },
	{ id: "milestones", labelKey: "project.milestones", icon: "flag" },
	{ id: "selections", labelKey: "project.selections", icon: "image" },
	{ id: "documents", labelKey: "project.documents", icon: "inbox" },
	{ id: "team", labelKey: "project.team", icon: "users" },
];

export const HOMEOWNER_TAB_IDS: readonly HomeownerTabId[] = HOMEOWNER_TABS.map(
	(tab) => tab.id,
);

// As above, for this surface. `tasks` and `activity` are absent because
// neither was ever a tab here, so this surface retired nothing to alias them
// to; `milestones` is absent because it is still a live tab here.
//
// `payments` was never a tab here either, and is a live tab on the designer
// surface. It is aliased because get-notification-route.ts sends the
// homeowner-only `project_awaiting_funding` notification to it, and the
// homeowner acts on that notification with the "Approve & fund milestone"
// button in HoMilestonesTab.
//
// This lands on a different tab from the one whose badge counts held and
// pending payments (BOQ, via pendingBoqBadge in ho-active-project-detail.tsx),
// which is deliberate: no escrow row exists yet at awaiting_funding. See the
// comment on pendingBoqBadge.
export const HOMEOWNER_RETIRED_TAB_IDS: Readonly<
	Record<string, HomeownerTabId>
> = {
	comments: "messages",
	costs: "boq",
	contractors: "team",
	payments: "milestones",
};
